import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, UserPlus } from "lucide-react";
import type { FormEvent } from "react";

import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, Organization } from "@/types";

interface GroupOption {
  key: string;
  name: string;
}

interface MembersNewProps {
  available_groups: GroupOption[];
  organization: Organization;
}

export default function MembersNew({ available_groups: availableGroups }: MembersNewProps) {
  const routes = useOrganizationRoutes();
  const { data, setData, post, processing, errors, transform } = useForm({
    email: "",
    group_keys: [] as string[],
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Miembros",
      href: routes.members.index(),
    },
    {
      title: "Nuevo",
      href: routes.members.new(),
    },
  ];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    transform((formData) => ({
      member: formData,
    }));
    post(routes.members.index());
  };

  const toggleGroup = (groupKey: string) => {
    const currentKeys = Array.isArray(data.group_keys) ? data.group_keys : [];
    if (currentKeys.includes(groupKey)) {
      setData(
        "group_keys",
        currentKeys.filter((key) => key !== groupKey)
      );
    } else {
      setData("group_keys", [...currentKeys, groupKey]);
    }
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Agregar miembro" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={routes.members.index()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Miembros
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Agregar miembro</h1>
            <p className="text-muted-foreground">
              Invitá a alguien con su email y definí sus roles.
            </p>
          </div>
        </div>

        <Card className="max-w-3xl">
          <CardHeader>
            <CardTitle>Datos del miembro</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={data.email}
                  onChange={(event) => setData("email", event.target.value)}
                  placeholder="usuario@empresa.com"
                  autoComplete="email"
                  disabled={processing}
                  aria-invalid={!!errors.email}
                />
                <InputError message={errors.email} />
              </div>

              <div className="space-y-3">
                <Label>Roles *</Label>
                <p className="text-sm text-muted-foreground">
                  Seleccioná al menos un rol para habilitar el acceso.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {availableGroups.map((group) => {
                    const checked = data.group_keys.includes(group.key);
                    return (
                      <label
                        key={group.key}
                        className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/10 p-3"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleGroup(group.key)}
                          disabled={processing}
                        />
                        <div>
                          <p className="text-sm font-medium leading-none">
                            {group.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {group.key}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <InputError message={errors.group_keys} />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" disabled={processing} className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  {processing ? "Guardando..." : "Agregar miembro"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={processing}
                  asChild
                >
                  <Link href={routes.members.index()}>
                    Cancelar
                  </Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
